import { Country } from '@trnsact/trnsact-shared-types';
import { AccountContactFormValues, accountContactFormDefaultValues } from '../ui/dialogs/accountContactFormSchema';
import {
  AddressInput,
  AddressEntity,
  AccountContactEntity,
  CreateAccountContactInput,
  UpdateAccountContactInput,
} from '../api/contact/types';

const mapAddressEntityToForm = (address: AddressEntity | null | undefined) => ({
  city: address?.city ?? '',
  zip: address?.postalCode ?? '',
  state: address?.stateProvince ?? '',
  streetAddress: address?.address ?? '',
  country: address?.country ?? Country.Us,
});

export const mapContactEntityToFormValues = (contact: AccountContactEntity): AccountContactFormValues => {
  const contactAddress = contact.contactAddress;
  const deliveryAddress = contact.deliveryAddress;

  const hasSameAddress =
    !deliveryAddress ||
    (contactAddress?.address === deliveryAddress?.address &&
      contactAddress?.city === deliveryAddress?.city &&
      contactAddress?.stateProvince === deliveryAddress?.stateProvince &&
      contactAddress?.postalCode === deliveryAddress?.postalCode &&
      contactAddress?.country === deliveryAddress?.country);

  return {
    ...accountContactFormDefaultValues,
    email: contact.email ?? '',
    phone: contact.phone ?? '',
    title: contact.title ?? '',
    sameAsContact: hasSameAddress,
    lastName: contact.lastName ?? '',
    firstName: contact.firstName ?? '',
    contactAddress: mapAddressEntityToForm(contactAddress),
    deliveryAddress: mapAddressEntityToForm(deliveryAddress),
  };
};

const mapAddressToInput = (address: AccountContactFormValues['contactAddress']): AddressInput => ({
  city: address.city,
  postalCode: address.zip,
  stateProvince: address.state,
  address: address.streetAddress,
  country: address.country ?? Country.Us,
});

const resolveDeliveryAddress = (values: AccountContactFormValues): AddressInput => {
  if (values.sameAsContact) {
    return mapAddressToInput(values.contactAddress);
  }

  return mapAddressToInput(values.deliveryAddress as AccountContactFormValues['contactAddress']);
};

export const mapFormToCreateInput = (
  values: AccountContactFormValues,
  accountId: string
): CreateAccountContactInput => ({
  accountId,
  lastName: values.lastName || null,
  email: values.email || null,
  phone: values.phone || null,
  firstName: values.firstName,
  deliveryAddress: resolveDeliveryAddress(values),
  contactAddress: mapAddressToInput(values.contactAddress),
});

export const mapFormToUpdateInput = (
  values: AccountContactFormValues,
  contactId: string
): UpdateAccountContactInput => ({
  id: contactId,
  lastName: values.lastName || null,
  firstName: values.firstName,
  email: values.email || null,
  phone: values.phone || null,
  title: values.title || null,
  deliveryAddress: resolveDeliveryAddress(values),
  contactAddress: mapAddressToInput(values.contactAddress),
});
